import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { NavLink } from '../types';
import logoImage from '../assets/airy.webp';
import { colors, typography, borderRadius, layout } from '../styles/designTokens';

const AccessibilityPage = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const navLinks: NavLink[] = [
    { label: 'בית', href: '/', isActive: false },
    { label: 'ממליצים', href: '/#testimonials', isActive: false },
    { label: 'מפרט המכונה', href: '/#product-specs', isActive: false },
    { label: 'נקודות מכירה', href: '/#branches', isActive: false },
    { label: 'צור קשר', href: '/#contact', isActive: false },
  ];

  return (
    <div
      className="min-h-screen scroll-smooth bg-airy-main"
      style={{
        fontFamily: typography.fontFamily,
      }}
    >
      {/* Skip to main content link */}
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          left: '-9999px',
          zIndex: 999,
          padding: '12px 24px',
          backgroundColor: colors.orangeGold,
          color: '#000000',
          fontWeight: 700,
          fontSize: '1rem',
          textDecoration: 'none',
          borderRadius: '4px',
        }}
        onFocus={(e) => {
          e.currentTarget.style.left = '50%';
          e.currentTarget.style.top = '10px';
          e.currentTarget.style.transform = 'translateX(-50%)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.left = '-9999px';
          e.currentTarget.style.top = 'auto';
          e.currentTarget.style.transform = 'none';
        }}
      >
        דלג לתוכן הראשי
      </a>

      <Header
        logoImage={logoImage}
        navLinks={navLinks}
        isMobileMenuOpen={isMobileMenuOpen}
        onMobileMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />

      {/* Main Content */}
      <main id="main-content" className="py-8 md:py-12" style={{ 
        paddingTop: 'clamp(90px, calc(5vw + 70px), 110px)',
        paddingBottom: '40px',
      }}>
        <div className="container mx-auto px-4" style={{ maxWidth: layout.contentMaxWidth }}>
          <div className="max-w-5xl mx-auto">
            {/* Back Button - כפתור חזרה */}
            <div style={{ marginBottom: 'clamp(16px, 2.5vw, 24px)' }}>
              <a
                href="/payment"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: colors.orangeGold,
                  fontSize: 'clamp(0.95rem, 1.8vw, 1.1rem)',
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  padding: '8px 12px',
                  borderRadius: '8px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = colors.gold.mediumLight;
                  e.currentTarget.style.background = 'rgba(245, 182, 88, 0.1)';
                  e.currentTarget.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = colors.orangeGold;
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <span style={{ fontSize: '1.2em', lineHeight: '1' }}>→</span>
                <span>חזרה לעמוד התשלום</span>
              </a>
            </div>
            
            {/* Page Title */}
            <h1 
              className="text-center mb-6 md:mb-8"
              style={{ 
                color: colors.orangeGold,
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                fontWeight: 900,
                lineHeight: 1.2,
                letterSpacing: typography.heading.letterSpacing,
                scrollMarginTop: '80px',
              }}
            >
              הצהרת נגישות
            </h1>
            
            {/* Main Content Box */}
            <div 
              className="p-4 md:p-5 rounded-xl"
              style={{ 
                backgroundColor: '#000000',
                border: '2px solid #FFFFFF',
                borderRadius: borderRadius.soft,
              }}
            >
              {/* Introduction */}
              <div 
                className="mb-8 p-4 md:p-6 rounded-xl"
                style={{ 
                  backgroundColor: '#1a1a1a',
                  border: `1.5px solid ${colors.orangeGold}`,
                  borderRadius: borderRadius.soft,
                }}
              >
                <p 
                  className="text-center"
                  style={{ 
                    color: colors.textPrimary,
                    fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                    fontWeight: 400,
                    lineHeight: typography.body.lineHeight,
                    letterSpacing: typography.body.letterSpacing,
                  }}
                >
                  מתוך אמונה שכל אדם זכאי ליהנות משירות שוויוני, עצמאי ונגיש. אנו עושים את מירב המאמצים כדי שהשירותים המוצעים באתר יהיו נגישים ככל האפשר, ויעמדו בדרישת תקנות שיוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות) התשע"ג 2013, ברמת התקן הנדרש.
                </p>
              </div>

              {/* Content Grid */}
              <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                {/* Left Column */}
                <div className="space-y-4 md:space-y-6">
                  <div 
                    className="p-4 md:p-6 rounded-xl"
                    style={{ 
                      backgroundColor: '#1a1a1a',
                      border: `1.5px solid ${colors.orangeGold}`,
                      borderRadius: borderRadius.soft,
                    }}
                  >
                    <h2 
                      className="mb-4"
                      style={{ 
                        color: colors.orangeGold,
                        fontSize: 'clamp(1.25rem, 2.5vw, 1.5rem)',
                        fontWeight: 900,
                        lineHeight: 1.2,
                        letterSpacing: typography.heading.letterSpacing,
                      }}
                    >
                      נגישות האתר
                    </h2>
                  <ul className="space-y-3" style={{ listStyle: 'none', padding: 0 }}>
                    {[
                      'האתר מותאם לשימוש בדפדפנים הנפוצים.',
                      'ניתן לשנות את גודל הטקסט באמצעות זום הדפדפן (Ctrl + או Ctrl -).',
                      'האתר מותאם בצורה רספונסיבית.',
                      'מבנה האתר פשוט וברור לניווט.',
                      'תוכן האתר כתוב בצורה ברורה ובשפה פשוטה.',
                      'הצבעים באתר נבחרו תוך הקפדה על ניגודיות מספיקה',
                    ].map((item, index) => (
                      <li key={index} className="flex items-start" style={{ gap: layout.gap.small }}>
                        <span style={{ color: colors.orangeGold, fontSize: '1.5rem' }}>•</span>
                        <span style={{ color: colors.textPrimary, fontSize: typography.fontSize.base }}>
                          {item}
                        </span>
                    </li>
                    ))}
                    </ul>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4 md:space-y-6">
                  <div 
                    className="p-4 md:p-6 rounded-xl"
                    style={{ 
                      backgroundColor: '#1a1a1a',
                      border: `1.5px solid ${colors.orangeGold}`,
                      borderRadius: borderRadius.soft,
                    }}
                  >
                    <h2 
                      className="mb-4"
                      style={{ 
                        color: colors.orangeGold,
                        fontSize: 'clamp(1.25rem, 2.5vw, 1.5rem)',
                        fontWeight: 900,
                        lineHeight: 1.2,
                        letterSpacing: typography.heading.letterSpacing,
                      }}
                    >
                      תכונות נוספות
                    </h2>
                  <ul className="space-y-3" style={{ listStyle: 'none', padding: 0 }}>
                    {[
                      'האתר מותאם לתצוגה תואמת מגוון מסכים ורזולוציות.',
                      'כל הדפים באתר בעלי מבנה קבוע.',
                      'למרבית התמונות באתר יש הסבר טקסטואלי חלופי (alt).',
                      'אין באתר שימוש בתצוגת טקסט מהבהב.',
                    ].map((item, index) => (
                      <li key={index} className="flex items-start" style={{ gap: layout.gap.small }}>
                        <span style={{ color: colors.orangeGold, fontSize: '1.5rem' }}>•</span>
                        <span style={{ color: colors.textPrimary, fontSize: typography.fontSize.base }}>
                          {item}
                        </span>
                    </li>
                    ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Contact Section */}
              <div 
                className="mt-8 p-6 md:p-8 rounded-xl text-center"
                style={{ 
                  backgroundColor: '#1a1a1a',
                  border: `1.5px solid ${colors.orangeGold}`,
                  borderRadius: borderRadius.soft,
                }}
              >
                <h2 
                  className="mb-4"
                  style={{ 
                    color: colors.orangeGold,
                    fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                    fontWeight: 900,
                    lineHeight: 1.2,
                    letterSpacing: typography.heading.letterSpacing,
                  }}
                >
                  צרו קשר
                </h2>
                <p 
                  className="mb-6"
                  style={{ 
                    color: colors.textPrimary,
                    fontSize: 'clamp(0.95rem, 2vw, 1.125rem)',
                    fontWeight: 400,
                    lineHeight: typography.body.lineHeight,
                  }}
                >
                  במידה ונתקלתם בקושי בגלישה באתר/צפייה בתוכנו למרות מאמצינו, נשמח כי תפנו את תשומת ליבנו לכך, בפניה מסודרת לכתובת המייל - כדאי לפרט כמה שיותר אודות הבעיה. כמובן שנעשה ככל שביכולתנו כדי לענות לפניה בצורה מקצועית ומהירה.
                </p>
                <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <h3 
                      className="mb-2"
                      style={{ 
                        color: colors.orangeGold,
                        fontSize: 'clamp(1.125rem, 2vw, 1.25rem)',
                        fontWeight: 700,
                      }}
                    >
                      טלפון
                    </h3>
                    <p 
                      style={{ 
                        color: colors.textPrimary,
                        fontSize: typography.fontSize.lg,
                        fontWeight: 400,
                      }}
                    >
                      073.302.6674
                    </p>
                  </div>
                  <div>
                    <h3 
                      className="mb-2"
                      style={{ 
                        color: colors.orangeGold,
                        fontSize: 'clamp(1.125rem, 2vw, 1.25rem)',
                        fontWeight: 700,
                      }}
                    >
                      דוא"ל
                    </h3>
                    <p 
                      style={{ 
                        color: colors.textPrimary,
                        fontSize: typography.fontSize.lg,
                        fontWeight: 400,
                      }}
                    >
                      yam0733026674@gmail.com
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AccessibilityPage;
